import { computed, type WritableComputedRef } from 'vue'
import type { NestedKeyOf } from '@/features/shared/types'
import {
  objectGetValueWithNestedKeyArray,
  objectSetValueWithNestedKey,
  objectSetValueWithNestedKeyArray,
} from '@/features/shared/tools/object'
import { useDebounce } from '@/features/shared/tools/debounce'
import { createDraft, enablePatches, finishDraft, type Draft, type Patch } from 'immer'

enablePatches()

function calculeModeleEtPatchs<TModel extends object>(patch: Draft<TModel>): [TModel, Patch[]] {
  let newPatchs
  const newModel = finishDraft(patch, (patchs) => (newPatchs = patchs))
  return [newModel as TModel, newPatchs!]
}

export type ImmerFieldPatcherOptions<TModel extends object> = {
  getModel: () => TModel
  setModel: (model: TModel, patch: Patch[]) => Promise<void> | void
  debounceDelay?: number
}

export type ImmerComputedBuilderOptions<TModel extends object, TResult> = {
  getValue(model: TModel): TResult
  setPatch(patch: Draft<TModel>, value: TResult): void | Promise<void>
  withDebounce?: boolean
}

export type ImmerCustomUpdaterCallback<TModel extends object, TParam> = (
  patch: Draft<TModel>,
  parametres: TParam,
) => void | Promise<void>

export type ImmerComputedBuilderWithKey<TModel extends object> = <TValue>(
  path: NestedKeyOf<TModel>,
  withDebounce?: boolean,
) => WritableComputedRef<TValue>

export type ImmerComputedBuilderWithOption<TModel extends object> = <TValue>(
  options: ImmerComputedBuilderOptions<TModel, TValue>,
) => WritableComputedRef<TValue>

export type ImmerArrayUpdateBuilderWithKey<TModel extends object> = <TModelArray extends object>(
  arrayPath: NestedKeyOf<TModel>,
) => (fieldPath: NestedKeyOf<TModelArray>, index: number, eventArg?: any) => void | Promise<void>

export type ImmerCustomUpdater<TModel extends object> = <TParams>(
  setPatch: ImmerCustomUpdaterCallback<TModel, TParams>,
) => (parametres: TParams) => Promise<void> | void

export function useImmerFieldPatcher<TModel extends object>(
  storeModelPatcherOptions: ImmerFieldPatcherOptions<TModel>,
) {
  /**
   * Construit un computed pour un objet model du store basé sur des les lambda get et setPatch
   * @param options Getter de la valeur et Setter du patch du modéle
   * @returns Un computed en lecture ecriture (WritableComputedRef<T>)
   * @description ATTENTION :
   * Les setters ne sont pas "awaitable", si le patch ou le setter est async dans certains cas,
   * les modifications peuvent être perdue
   *
   * @example
   *  // Ne pas faire, utiliser plutot setupArrayUpdaterWithKey ou setupCustomUpdater
   *  // qui sont tout deux "awaitable"
   *  const modelA = setupFieldWithKey<string>('modelA', true) // si async
   *  const modelB = setupFieldWithOptions<string>('modelB', true) // si async
   *  modelA.value = 'A'
   *  modelB.value = 'B'
   *
   *  Les modifications apportées au modelA serait perdu à cause du debounce
   */
  const setupFieldWithKey: ImmerComputedBuilderWithKey<TModel> = function <TValue>(
    fieldPath: NestedKeyOf<TModel>,
    withDebounce?: boolean,
  ) {
    // Optimisation
    const debounce = useDebounce(storeModelPatcherOptions.debounceDelay ?? 500)
    const fieldPathTokens = fieldPath.split('.')
    function updater(value: any) {
      const model = storeModelPatcherOptions.getModel()
      const patch = objectSetValueWithNestedKeyArray(createDraft(model), fieldPathTokens, value)
      return calculeModeleEtPatchs(patch)
    }
    return computed<TValue>({
      get() {
        const model = storeModelPatcherOptions.getModel()
        return objectGetValueWithNestedKeyArray(model, fieldPathTokens)
      },
      async set(value) {
        if (withDebounce) {
          debounce(() => {
            storeModelPatcherOptions.setModel(...updater(value))
          })
        } else {
          await storeModelPatcherOptions.setModel(...updater(value))
        }
      }
    })
  }

  /**
   * Construit un computed pour un objet model du store basé sur des les lambda get et setPatch
   * @param options Getter de la valeur et Setter du patch du modéle
   * @returns Un computed en lecture ecriture (WritableComputedRef<T>)
   * @description ATTENTION :
   * Les setters ne sont pas "awaitable", si le patch ou le setter est async dans certains cas,
   * les modifications peuvent être perdue
   *
   * @example
   *  // Ne pas faire, utiliser plutot setupArrayUpdaterWithKey ou setupCustomUpdater
   *  // qui sont tout deux "awaitable"
   *  const modelA = setupFieldWithOptions<string>({ ... }) // si async
   *  const modelB = setupFieldWithOptions<string>({ ... }) // si async ou pas
   *  modelA.value = 'A'
   *  modelB.value = 'B'
   *
   *  Les modifications apportées au modelA vont être perdue
   */
  const setupFieldWithOptions: ImmerComputedBuilderWithOption<TModel> = function <TValue>(
    options: ImmerComputedBuilderOptions<TModel, TValue>,
  ) {
    const debounce = useDebounce(storeModelPatcherOptions.debounceDelay ?? 500)
    return computed<TValue>({
      get() {
        const model = storeModelPatcherOptions.getModel()
        try {
          return options.getValue(model)
        } catch (error) {
          console.error(`setupFieldWithOptions a échoué sur l'appel à getValue()`, error)
          return undefined as TValue
        }
      },
      set(value) {
        try {
          const model = storeModelPatcherOptions.getModel()
          const patch = createDraft(model)
          const setPatchResult = options.setPatch(patch, value)
          const applyPatch = () => {
            const [newModel, newPatchs] = calculeModeleEtPatchs(patch)
            return storeModelPatcherOptions.setModel(newModel, newPatchs)
          }
          if (setPatchResult instanceof Promise) {
            setPatchResult
              .then(() => {
                if (options.withDebounce) {
                  debounce(applyPatch)
                } else {
                  return applyPatch()
                }
              })
              .catch((error) => logErreurOptions(value, error))
            return
          }
          if (options.withDebounce) {
            debounce(applyPatch)
          } else {
            const setModelResult = applyPatch()
            if (setModelResult instanceof Promise) {
              setModelResult.catch((error) => logErreurOptions(value, error))
              return
            }
          }
        } catch (error) {
          // Traitement synchone
          logErreurOptions(value, error)
          throw error
        }
      },
    })
  }

  /**
   * Construit une fonction d'update d'un array les clés du tableau et du membre à updater
   * @param arrayPath Clés du tableau
   * @returns Une fonction d'update
   */

  const setupArrayUpdaterWithKey: ImmerArrayUpdateBuilderWithKey<TModel> = function <TModelArray extends object>(
    arrayPath: NestedKeyOf<TModel>,
  ) {
    // Optimisation
    const arrayPathTokens = arrayPath.split('.')
    return function (fieldPath: NestedKeyOf<TModelArray>, index: number, eventArg?: any) {
      const model = storeModelPatcherOptions.getModel()
      const patch = createDraft(model)
      const patchArray = objectGetValueWithNestedKeyArray<Draft<TModel>, Array<TModelArray>>(patch, arrayPathTokens)

      eventArg = extraitEventArg(eventArg)
      objectSetValueWithNestedKey(patchArray[index]!, fieldPath, eventArg)

      const [newModel, newPatchs] = calculeModeleEtPatchs(patch)
      const result = storeModelPatcherOptions.setModel(newModel, newPatchs)
      if (result instanceof Promise) {
        return result
      }
      return undefined as void
    }
  }

  const setupCustomUpdater: ImmerCustomUpdater<TModel> = function <TParams>(
    setPatch: ImmerCustomUpdaterCallback<TModel, TParams>,
  ) {
    return function (parametres: TParams) {
      const model = storeModelPatcherOptions.getModel()
      const patch = createDraft(model)
      const catchError = (error: any) => {
        logErreurOptions(parametres, error)
        throw error
      }
      try {
        const setPatchResult = setPatch(patch, parametres)
        if (setPatchResult instanceof Promise) {
          return setPatchResult
            .then(() => {
              const [newModel, newPatchs] = calculeModeleEtPatchs(patch)
              return storeModelPatcherOptions.setModel(newModel, newPatchs)
            })
            .catch(catchError)
        }
        const [newModel, newPatchs] = calculeModeleEtPatchs(patch)
        const patchModelResult = storeModelPatcherOptions.setModel(newModel, newPatchs)
        if (patchModelResult instanceof Promise) {
          return patchModelResult.catch(catchError)
        }
      } catch (error) {
        logErreurOptions(parametres, error)
        throw error
      }
    }
  }

  return { setupFieldWithKey, setupFieldWithOptions, setupArrayUpdaterWithKey, setupCustomUpdater }
}

function logErreurOptions(value: any, error: unknown) {
  console.error(`Le patch setter options a échoué sur l'affectation de la valeur "${value}" avec l'erreur : ${error}.`)
}

function extraitEventArg(eventArg?: any) {
  // Si eventArg est un Evenement, obtient la valeur
  if (eventArg instanceof Event && eventArg.target instanceof HTMLInputElement) {
    if (eventArg.target.type === 'checkbox') {
      if (eventArg.target.indeterminate) {
        eventArg = undefined
      } else {
        eventArg = eventArg.target.checked
      }
    } else {
      eventArg = eventArg.target.value
    }
  }

  return eventArg
}
